import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Mail, ExternalLink } from 'lucide-react';
import { submissionsApi, newslettersApi, formConfigsApi, emailTemplatesApi, extractLookupId } from '../services/creatorApi';
type ConfirmState = 'loading' | 'success' | 'redirecting' | 'invalid' | 'error';
/** Returns true only if the URL is a real destination (not a Zoho Creator internal URL) */
function isValidRedirectUrl(url: string | undefined | null): boolean {
  if (!url || !url.trim()) return false;
  const u = url.trim();
  // Reject Zoho Creator app URLs — these are internal record permalinks, not redirect destinations
  if (/creatorapp\.zohopublic\.com/i.test(u)) return false;
  return /^https?:\/\//i.test(u);
}
/**
 * Look up the current Confirmation_Link from the email template via the newsletter chain.
 * Newsletter_ID → newsletter → Sign_Up_Form → form config → Email_Template → Confirmation_Link
 * This is always up-to-date regardless of what was baked into the embed code at generation time.
 */
async function lookupConfirmationLink(newsletterId: string): Promise<string | null> {
  try {
    console.log('[confirm] Looking up Confirmation_Link for Newsletter_ID:', newsletterId);
    const newsletters = await newslettersApi.list();
    const newsletter = newsletters.find(nl => nl.Newsletter_ID === newsletterId);
    if (!newsletter) {
      console.warn('[confirm] Newsletter not found for ID:', newsletterId);
      return null;
    }
    const formId = extractLookupId(newsletter.Sign_Up_Form);
    if (!formId) {
      console.warn('[confirm] No Sign_Up_Form on newsletter');
      return null;
    }
    const configs = await formConfigsApi.findByFormId(formId);
    if (!configs.length) {
      console.warn('[confirm] No form config found for formId:', formId);
      return null;
    }
    const templateId = extractLookupId(configs[0].Email_Template);
    if (!templateId) {
      console.warn('[confirm] No Email_Template on form config');
      return null;
    }
    const template = await emailTemplatesApi.get(templateId);
    if (!template) {
      console.warn('[confirm] Email template not found for ID:', templateId);
      return null;
    }
    const rawLink = (template as any).Confirmation_Link;
    const url = rawLink ? typeof rawLink === 'string' ? rawLink : rawLink?.url || null : null;
    console.log('[confirm] Found Confirmation_Link:', url);
    return url || null;
  } catch (err) {
    console.error('[confirm] lookupConfirmationLink failed:', err);
    return null;
  }
}
export function ConfirmSubscription() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const recordId = searchParams.get('id') || undefined;
  const [state, setState] = useState<ConfirmState>('loading');
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [errorDetail, setErrorDetail] = useState<string>('');
  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    const run = async () => {
      try {
        const result = await submissionsApi.confirm(token, recordId);
        if (!result.success) {
          setErrorDetail(result.errorDetail || result.error || 'Unknown error');
          setState(result.error === 'invalid_token' ? 'invalid' : 'error');
          return;
        }
        const storedRedirect = result.submission?.Redirect_URL;
        const newsletterId = result.submission?.Newsletter_ID;
        console.log('[confirm] storedRedirect:', storedRedirect);
        console.log('[confirm] newsletterId:', newsletterId);
        // Strategy 1: Use Redirect_URL from submission if it's a valid non-Zoho URL
        if (isValidRedirectUrl(storedRedirect)) {
          console.log('[confirm] Using stored Redirect_URL:', storedRedirect);
          setRedirectUrl(storedRedirect!.trim());
          setState('redirecting');
          return;
        }
        // Strategy 2: Redirect_URL is missing or is a Zoho Creator URL (stale embed code).
        // Look up the CURRENT Confirmation_Link from the email template — always up-to-date.
        if (newsletterId) {
          console.log('[confirm] Stored redirect invalid, looking up template Confirmation_Link...');
          const templateLink = await lookupConfirmationLink(newsletterId);
          if (isValidRedirectUrl(templateLink)) {
            console.log('[confirm] Using template Confirmation_Link:', templateLink);
            setRedirectUrl(templateLink!.trim());
            setState('redirecting');
            return;
          }
        }
        // Strategy 3: No valid redirect URL found — show success page
        console.log('[confirm] No redirect URL found, showing success page');
        setState('success');
      } catch (err: any) {
        setErrorDetail(err instanceof Error ? err.message : String(err));
        setState('error');
      }
    };
    run();
  }, [token, recordId]);
  // Countdown + redirect
  useEffect(() => {
    if (state !== 'redirecting' || !redirectUrl) return;
    if (countdown <= 0) {
      window.location.href = redirectUrl;
      return;
    }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [state, countdown, redirectUrl]);
  return <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" data-id="element-2099">
      <div className="w-full max-w-md" data-id="element-2100">
        {/* Loading */}
        {state === 'loading' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-2101">
            <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-5" data-id="element-2102">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-2103" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2104">
              Confirming your subscription...
            </h1>
            <p className="text-sm text-gray-500" data-id="element-2105">
              Please wait while we verify your email address.
            </p>
          </div>}

        {/* Redirecting with countdown */}
        {state === 'redirecting' && redirectUrl && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-2106">
            <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-5" data-id="element-2107">
              <CheckCircle2 className="h-8 w-8 text-green-500" data-id="element-2108" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2109">
              Subscription confirmed!
            </h1>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2110">
              Your email has been verified. You're now subscribed.
            </p>
            <div className="flex flex-col items-center gap-3 mb-6" data-id="element-2111">
              <div className="relative w-16 h-16" data-id="element-2112">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64" data-id="element-2113">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#e5e7eb" strokeWidth="4" data-id="element-2114" />
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#22c55e" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 28}`} strokeDashoffset={`${2 * Math.PI * 28 * (1 - (3 - countdown) / 3)}`} style={{
                transition: 'stroke-dashoffset 1s linear'
              }} data-id="element-2115" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-gray-800" data-id="element-2116">
                  {countdown}
                </span>
              </div>
              <p className="text-sm text-gray-500" data-id="element-2117">
                Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
            </div>
            <a href={redirectUrl} className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium underline underline-offset-2" data-id="element-2118">
              Click here if not redirected automatically
              <ExternalLink className="h-3.5 w-3.5" data-id="element-2119" />
            </a>
          </div>}

        {/* Success (no redirect URL) */}
        {state === 'success' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-2120">
            <div className="mx-auto w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-5" data-id="element-2121">
              <CheckCircle2 className="h-8 w-8 text-green-500" data-id="element-2122" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2123">
              Subscription confirmed!
            </h1>
            <p className="text-sm text-gray-500 mb-6" data-id="element-2124">
              Your email has been verified and your subscription is now active.
              You'll start receiving updates soon.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200" data-id="element-2125">
              <Mail className="h-4 w-4" data-id="element-2126" />
              You're all set!
            </div>
          </div>}

        {/* Invalid token */}
        {state === 'invalid' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-2127">
            <div className="mx-auto w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-5" data-id="element-2128">
              <AlertTriangle className="h-8 w-8 text-amber-500" data-id="element-2129" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2130">
              Invalid confirmation link
            </h1>
            <p className="text-sm text-gray-500" data-id="element-2131">
              This confirmation link is invalid or has already been used. Please
              try signing up again.
            </p>
            {errorDetail && <p className="mt-4 text-xs text-red-400 font-mono break-all bg-red-50 rounded p-2" data-id="element-2132">
                {errorDetail}
              </p>}
          </div>}

        {/* Error */}
        {state === 'error' && <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 text-center" data-id="element-2133">
            <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-5" data-id="element-2134">
              <XCircle className="h-8 w-8 text-red-500" data-id="element-2135" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-2136">
              Something went wrong
            </h1>
            <p className="text-sm text-gray-500 mb-5" data-id="element-2137">
              We couldn't process your confirmation. Please try again.
            </p>
            <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-2138">
              Try Again
            </button>
            {errorDetail && <p className="mt-4 text-xs text-red-400 font-mono break-all bg-red-50 rounded p-2" data-id="element-2139">
                {errorDetail}
              </p>}
          </div>}

        <p className="text-center text-xs text-gray-400 mt-6" data-id="element-2140">
          Powered by The Chosen Campaign Manager
        </p>
      </div>
    </div>;
}